import { useCallback, useState } from "react"

import { toAdjustmentPalletLabelParams } from "../utils"

import { useGetInventoryPalletsForPrint } from "./useGetInventoryPalletsForPrint"

import { useZplPrinting } from "@/hooks/useZplPrinting"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { generatePalletLabelZpl } from "@/utils/printing/generatePalletLabelZpl"

interface UseAdjustmentPalletLabelPrintingReturn {
  printPalletLabels: (productId: number, palletReferenceNumbers: number[]) => Promise<boolean>
  isPrinting: boolean
}

export const useAdjustmentPalletLabelPrinting = (): UseAdjustmentPalletLabelPrintingReturn => {
  const [isPrinting, setIsPrinting] = useState(false)

  const { fetchPallets } = useGetInventoryPalletsForPrint()
  const { printZpl } = useZplPrinting()
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const printPalletLabels = useCallback(
    async (productId: number, palletReferenceNumbers: number[]): Promise<boolean> => {
      setIsPrinting(true)

      try {
        const pallets = await fetchPallets(productId, palletReferenceNumbers)
        if (!pallets.length) {
          notify({
            type: NotificationType.Info,
            message: "No pallets found for printing",
          })
          return false
        }

        const sortedPallets = [...pallets].sort((a, b) => (a.referenceNbr ?? 0) - (b.referenceNbr ?? 0))

        for (const pallet of sortedPallets) {
          const success = await printZpl(generatePalletLabelZpl(toAdjustmentPalletLabelParams(pallet)))

          if (!success) return false
        }

        return true
      } finally {
        setIsPrinting(false)
      }
    },
    [fetchPallets, notify, printZpl]
  )

  return {
    printPalletLabels,
    isPrinting,
  }
}
